import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

const SCROLL_LOCK_CLASS = 'slide-panel-open';

/**
 * Generic right-side slide-over panel with a dimmed backdrop.
 *
 * Purely presentational — the host owns the `open` state:
 *
 * ```html
 * <button (click)="filtersOpen = true">Filtros</button>
 * <app-slide-panel [(open)]="filtersOpen" title="Filtros">
 *   ...projected content...
 * </app-slide-panel>
 * ```
 *
 * Styling lives in src/assets/scss/rb/_slide-panel.scss so it can use design
 * tokens. The panel itself is viewport-agnostic; callers decide when to render
 * it (e.g. only expose the trigger on mobile).
 */
@Component({
  selector: 'app-slide-panel',
  templateUrl: './slide-panel.component.html',
  styleUrls: ['./slide-panel.component.scss'],
  standalone: false,
})
export class SlidePanelComponent implements OnChanges, OnDestroy {
  /** Whether the panel is visible. Two-way bindable via `[(open)]`. */
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  /** Heading shown in the panel's sticky header. */
  @Input() title = '';

  /** Accessible label for the close button. */
  @Input() closeLabel = 'Cerrar panel';

  /**
   * When true, the panel renders inline (static, no backdrop, no transform) at
   * >= 768px and only behaves as a drawer below that. Lets a page keep its
   * filters visible on desktop and collapse them on mobile with one markup.
   */
  @Input() inlineOnDesktop = false;

  close(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.openChange.emit(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.setScrollLock(this.open);
    }
  }

  ngOnDestroy(): void {
    this.setScrollLock(false);
  }

  /**
   * Lock body scroll while the drawer is open. The CSS for the class only
   * applies below the mobile breakpoint, so on desktop (inline mode) this is
   * a harmless no-op.
   */
  private setScrollLock(locked: boolean): void {
    document.body.classList.toggle(SCROLL_LOCK_CLASS, locked);
  }
}
